import { collection, getDocs, getDoc, addDoc, doc, updateDoc, deleteField, deleteDoc, limit, orderBy, query, Timestamp, where, writeBatch, setDoc, serverTimestamp } from 'firebase/firestore';
import { firebase } from '../firebase-config';
import { BaseUrl, COLLECTIONS, DOMAINURL, ROLES } from '../config';
import axiosInstance from '../axios';
import { getAuth } from 'firebase/auth';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { generateP } from '../helpers';
import { getLoginToken } from '../storage';
import { updateStripeQuantity } from './employee';

const auth = getAuth();
const defaultHeaders = {
  "Content-Type": "application/json",
  withCredentials: true,
};


/**
 * Get Document list from client collection
 * @param {*} formData : can be use for filter in future
 * @param {*} onSuccess
 * @param {*} onError
 */
export const getClientsList = async (formData, onSuccess, onError) => {

  try {
    const colRef = collection(firebase, COLLECTIONS.CLIENT);

    const querySnapshot = await getDocs(colRef);

    const results = querySnapshot?.docs?.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    if (onSuccess) {
      onSuccess(results);
    }
  } catch (error) {
    console.error('Error getting documents:', error);
    if (onError) {
      onError(error);
    }
  }
};

export async function getCompanyClients(companyId, onSuccess, onError) {
  try {
    const clientsQuery = query(
      collection(firebase, COLLECTIONS.CLIENT),
      where("companyId", "==", companyId),
    );

    const querySnapshot = await getDocs(clientsQuery);

    if (querySnapshot.empty) {
      onSuccess && onSuccess({ status: true, data: null });
    } else {
      const clients = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      onSuccess && onSuccess({ status: true, data: clients });
    }
  } catch (error) {
    onError && onError({ status: false, error: error.message });
  }
}

/**
 * Get Document list from client collection
 * @param {*} formData : can be use for array ids
 * @param {*} onSuccess
 * @param {*} onError
 */
export const getClientsListbasedonIds = async (formData, onSuccess, onError) => {
  try {
    const results = await Promise.all(
      formData.map(async (id) => {
        const clientDoc = doc(collection(firebase, COLLECTIONS.CLIENT), id);
        const docSnapshot = await getDoc(clientDoc);

        if (docSnapshot.exists()) {
          return { id: docSnapshot.id, ...docSnapshot.data() };
        } else {
          console.warn(`Document with ID ${id} does not exist.`);
          return null;
        }
      })
    );

    const filteredResults = results.filter(item => item !== null);

    if (onSuccess) {
      onSuccess(filteredResults);
    }
  } catch (error) {
    console.error('Error getting documents:', error);
    if (onError) {
      onError(error);
    }
  }
};
/**
 * Get details of perticular client
 * @param {*} id : id of client want to get data of
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function getClient(id, onSuccess, onError) {
  try {
    const docRef = doc(firebase, COLLECTIONS.CLIENT, id);
    const docSnapshot = await getDoc(docRef);

    if (!docSnapshot.exists()) {
      onError && onError({ status: false, error: 'Client not found' });
      return;
    }

    const data = docSnapshot.data();
    onSuccess && onSuccess({ status: true, data: { client_id: docSnapshot.id, ...data } });
  } catch (error) {
    onError && onError({ status: false, error });
  }
}

export async function RemoveUserInvite(email, onSuccess, onError) {
  const currentUser = auth.currentUser;
  const currentUserId = currentUser ? currentUser.uid : null;
  try {
    const usersQuery = query(
      collection(firebase, COLLECTIONS.USERS),
      where("email", "==", email)
    );

    const snapshot = await getDocs(usersQuery);
    const userDoc = snapshot?.docs?.find((doc) => doc.data()?.deleted !== true);

    const userId = userDoc.id;
    const firstName = userDoc?.data()?.firstName;
    const email = userDoc?.data()?.email || "";

    const userRef = doc(firebase, COLLECTIONS.USERS, userId);

    await updateDoc(userRef, {
      deleted: true,
      status: false,
      updatedBy: currentUserId,
      deletedBy: currentUserId,
      deletedAt: serverTimestamp(),
    });

    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    await axiosInstance.post(
      `${BaseUrl}removeUser`,
      { id: userId, firstName, email },
      { headers: { ...defaultHeaders, ...authHeaders } }
    );

    onSuccess && onSuccess({ status: true, message: "User invite revoked successfully." });
    return { status: true };
  } catch (err) {
    console.error("RemoveUserInvite failed:", err.message);
    onError && onError({ status: false, message: err.message });
    return { status: false, message: err.message };
  }
}

export async function InviteUser(id, contactItem, type = "primary", action = "invite", companyName, createdBy, subscriptionId, subscriptionItemId, onSuccess, onError) {
  const currentUser = auth.currentUser;
  const currentUserId = currentUser ? currentUser.uid : null;

  const clientsQuery = collection(firebase, COLLECTIONS.CLIENT);
  const clientsSnap = await getDocs(clientsQuery);

  const matchedClients = [];
  clientsSnap?.forEach(docSnap => {
    const data = docSnap.data();
    const contacts = data?.contact || [];
    const emailMatch = contacts?.some(c => c?.email === contactItem?.email);
    const idMatch = docSnap.id === id;
    if (emailMatch || idMatch) {
      matchedClients.push({ id: docSnap.id, ...data });
    }
  });

  const uniqueMatchedClients = Array.from(new Map(matchedClients?.map(client => [client.id, client]))?.values());


  if (uniqueMatchedClients?.length === 0) {
    onError && onError({ status: false, message: "Client Not Found" });
    return;
  }

  for (const client of uniqueMatchedClients) {
    const updatedContacts = (client?.contact || [])?.map(c =>
      c?.email === contactItem?.email
        ? { ...c, invite: action === "invite" }
        : c
    );

    await updateDoc(doc(firebase, COLLECTIONS.CLIENT, client.id), {
      contact: updatedContacts,
      updatedAt: Date.now(),
    });
  }
  const existingData = uniqueMatchedClients?.find((item) => item?.id === id);
  if (action === "invite") {
    const password = generateP();
    const fullName = contactItem?.name?.trim() || "";

    let firstName = "";
    let lastName = "";

    if (fullName) {
      const parts = fullName.split(" ");
      if (parts?.length === 1) {
        firstName = fullName;
        lastName = fullName;
      } else {
        firstName = parts?.[0];
        lastName = parts?.slice(1)?.join(" ");
      }
    }

    const newUserData = {
      firstname: firstName || "",
      lastname: lastName,
      email: contactItem?.email,
      phoneNumber: contactItem?.mobileNumber,
      password,
      companyId: existingData?.companyId,
      companyName: companyName,
      createdBy: createdBy,
    };

    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };

    try {
      const res = await axiosInstance.post(
        `${BaseUrl}createClientUser`,
        newUserData,
        { headers: { ...defaultHeaders, ...authHeaders } }
      );

      if (res?.data?.status === true) {
        const newClientUser = {
          role: ROLES.CLIENT,
          userId: res?.data?.uid,
          firstName: newUserData?.firstname,
          lastName: newUserData?.lastname,
          email: newUserData?.email,
          userName: newUserData?.email,
          phoneNumber: newUserData?.phoneNumber,
          timeZone: existingData?.timeZone || "",
          companyId: newUserData?.companyId || existingData?.companyId,
          client: id,
          status: true,
        };

        const userDocRef = doc(firebase, COLLECTIONS.USERS, newClientUser?.userId);
        await setDoc(userDocRef, {
          ...newClientUser,
          createdBy: currentUserId,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        });

        // await updateDoc(clientDocRef, {
        //   contact: updatedContacts,
        //   updatedAt: Date.now(),
        // });

        await updateStripeQuantity(subscriptionId, subscriptionItemId, newClientUser?.companyId);
        onSuccess && onSuccess({ status: true, message: "User invited successfully" });
      } else {
        let errorMessage = res?.data?.message || "Unknown error";
        if (res?.data?.error?.code === "auth/email-already-exists") {
          errorMessage = "This email is already registered.";
        }
        onError && onError({ status: false, error: errorMessage });
      }
    } catch (err) {
      onError && onError({ status: false, message: err.message });
    }
  }

  // === 🔁 REVOKE ===
  else if (action === "revoke") {
    try {
      const usersQuery = query(
        collection(firebase, COLLECTIONS.USERS),
        where("email", "==", contactItem.email),
        where("client", "==", id)
      );
      const snapshot = await getDocs(usersQuery);
      const userDoc = snapshot?.docs?.find((doc) => doc.data()?.deleted !== true);

      if (!userDoc) {
        onError && onError({ status: false, message: "No active user found to revoke" });
        return;
      }

      const userId = userDoc.id;
      const firstName = userDoc?.data()?.firstName;
      const email = userDoc?.data()?.email || "";
      const companyId = userDoc?.data()?.companyId || "";
      const userRef = doc(firebase, COLLECTIONS.USERS, userId);

      await updateDoc(userRef, {
        deleted: true,
        status: false,
        updatedBy: currentUserId,
        deletedBy: currentUserId,
        deletedAt: serverTimestamp(),
      });

      const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
      await axiosInstance.post(
        `${BaseUrl}removeUser`,
        { id: userId, firstName, email },
        { headers: { ...defaultHeaders, ...authHeaders } }
      );

      // await updateDoc(clientDocRef, {
      //   contact: updatedContacts,
      //   updatedAt: Date.now(),
      // });

      await updateStripeQuantity(subscriptionId, subscriptionItemId, companyId);

      onSuccess && onSuccess({ status: true, message: "User invite revoked successfully" });
    } catch (err) {
      onError && onError({ status: false, message: err.message });
    }
  }
}

export async function ReInviteUser(formData, onSuccess, onError) {
  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
  const { email, password, name, companyName, createdBy } = formData;

  const htmlContent = `
    <div style="font-family: Arial, sans-serif; color: #333; max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 10px; overflow: hidden;">
            <div style="background-color:#f4f6f8; padding:20px; text-align:center;">
              <img
                src="https://${DOMAINURL}/csmGrayTextLogin.png"
                alt="ProTeams Logo"
                width="180"
                style="display:block; margin:0 auto; max-width:180px; width:100%; height:auto; border:0;"
              />
            </div>

            <div style="padding:25px 20px;">
        <h2 style="color: #078037;">Welcome to ProTeams Web Portal!</h2>
        <p style="font-size: 16px;"><strong>Hello ${name},</strong></p>
        <p style="font-size: 15px;">You have been Re-Invited to access the <strong>ProTeams</strong> Web Portal. Your account has been successfully created and you can now log in to manage your content.</p>
        <hr style="margin: 20px 0;">
        <p style="font-size: 15px;">Please use the following credentials to log in to your Web Portal:</p>
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; border-left: 4px solid #078037; margin: 15px 0;">
          <p style="font-size: 16px; margin: 5px 0;"><strong>Email/Username:</strong> ${email}</p>
          <p style="font-size: 16px; margin: 5px 0;"><strong>Password:</strong> ${password}</p>
        </div>
        <div style="text-align: center; margin: 25px 0;">
          <a href="https://${DOMAINURL}/login" 
            style="background-color: #078037; color: white; padding: 12px 25px; text-decoration: none; border-radius: 5px; font-weight: bold; display: inline-block;">
            Login to Web Portal
          </a>
        </div>
        <p style="font-size: 14px; color: #666;">
          If you have any questions about using the Web Portal or need assistance getting started, please don't hesitate to reach out to our support team.
        </p>
        <hr style="border:none; border-top:1px solid #e5e7eb; margin:30px 0;">

              <p style="text-align:center; font-size:13px; color:#666; margin:0;">
                For assistance, feel free to <a href="https://${DOMAINURL}/help/support" style="color:#1a73e8; text-decoration:underline;">contact support</a>.<br>
                © ${new Date().getFullYear()} ProTeams. All rights reserved.
              </p>
    </div>
    </div>
  `;

  try {
    await Promise.all([
      axiosInstance.post(
        `${BaseUrl}updateUserPass`,
        { email, password },
        { headers: { ...defaultHeaders, ...authHeaders } }
      ),
      axiosInstance.post(
        `${BaseUrl}sendEmailData`,
        {
          email,
          subject: "You've been Re-Invited to ProTeams Web Portal",
          contenttxt: htmlContent,
        },
        {
          headers: {
            "Content-Type": "application/json",
            withCredentials: true,
            ...authHeaders,
          },
        }
      ),
    ]);

    onSuccess && onSuccess();
  } catch (err) {
    console.error("Reinvite failed:", err);
    onError && onError({ message: err?.message || "Reinvite failed" });
  }
}

export async function saveLogins(userId) {
  try {
    const historyRef = collection(firebase, COLLECTIONS.HISTORY);
    await addDoc(historyRef, {
      userId,
      login: true,
      createdAt: serverTimestamp()
    });
  } catch (err) {
    console.error("Error saving login:", err);
  }
}

export async function addPreferenceUser(userId, newItemsPerPage) {
  try {
    const userRef = doc(firebase, COLLECTIONS.USERS, userId);
    await setDoc(
      userRef,
      { preference: newItemsPerPage },
      { merge: true }
    );
  } catch (err) {
    console.error("Error saving data:", err);
  }
}

export async function syncClientContactLogin(userId, email) {
  try {
    const userRef = doc(firebase, COLLECTIONS.USERS, userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) return;

    const userData = userSnap.data();

    if (userData?.role !== "Client") return;

    const clientId = userData?.client;
    if (!clientId) return;

    const clientDocRef = doc(firebase, COLLECTIONS.CLIENT, clientId);
    const clientSnap = await getDoc(clientDocRef);
    if (!clientSnap.exists()) return;

    const clientData = clientSnap.data();
    const contacts = clientData?.contact || [];

    let contactUpdated = false;

    const updatedContacts = contacts.map((c) => {
      if (c.email === email && c.login !== true) {
        contactUpdated = true;
        return { ...c, login: true };
      }
      return c;
    });

    if (contactUpdated) {
      await updateDoc(clientDocRef, {
        contact: updatedContacts,
        updatedAt: new Date(),
      });
    }
  } catch (err) {
    console.warn("Background login sync failed:", err.message);
  }
}

export async function findClientsByEmail(email) {
  try {
    const clientsRef = collection(firebase, COLLECTIONS.CLIENT);
    const clientsSnap = await getDocs(clientsRef);

    const matchedClients = [];
    clientsSnap.forEach((docSnap) => {
      const data = docSnap.data();
      const contacts = data?.contact || [];
      if (contacts?.some(c => c?.email === email)) {
        matchedClients?.push({ id: docSnap.id, ...data });
      }
    });

    if (matchedClients?.length === 0) {
      return { companies: [], clients: [] };
    }

    const uniqueCompanyIds = [...new Set(matchedClients?.map(c => c.companyId))];

    if (uniqueCompanyIds?.length === 1) {
      return { companies: [], clients: matchedClients };
    }

    const companyRecords = [];
    for (const companyId of uniqueCompanyIds) {
      const companyDocRef = doc(firebase, COLLECTIONS.USERS, companyId);
      const companySnap = await getDoc(companyDocRef);
      if (companySnap.exists()) {
        companyRecords.push({ id: companySnap.id, ...companySnap.data() });
      }
    }
    return { companies: companyRecords, clients: matchedClients };
  } catch (err) {
    console.error("Error finding clients by email:", err);
    return { companies: [], clients: [] };
  }
}

/**
 * Create a new client
 * @param {*} formData Data to save in firestore
 * @param {*} acuityClientData Data to save in acuity data
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function addClient(formData, acuityClientData, onSuccess, onError) {
  try {
    const resultNumberQuery = query(
      collection(firebase, COLLECTIONS.CLIENT),
      orderBy('clientNumber', 'desc'),
      limit(1)
    );
    const resultNumber = await getDocs(resultNumberQuery);

    let newClientNumber = 101;

    resultNumber.forEach((doc) => {
      const apptData = doc.data();
      newClientNumber = apptData.clientNumber + 1;
    });

    const currentUser = auth.currentUser;
    const currentUserId = currentUser ? currentUser.uid : null;

    const newClientData = {
      ...formData,
      status: false,
      role: ROLES.CLIENT,
      createdBy: currentUserId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      clientNumber: newClientNumber,
    };

    const docRef = await addDoc(collection(firebase, COLLECTIONS.CLIENT), newClientData);

    // const clientRes = await axiosInstance.post(`${BaseUrl}createClientAcuity`, {
    //   acuityClientData,
    // });

    // console.log('client acuity create', clientRes);

    const responseItem = { ...newClientData, id: docRef.id };
    onSuccess && onSuccess({ status: true, data: responseItem });
    return { status: true, data: responseItem };

  } catch (error) {
    console.log(`ERROR: ${error.message || error}`);
    onError && onError({ status: false, error: error.message || error });
  }
}

function getFieldsToUpdate(existingData, formData) {
  const fieldsToUpdate = {};
  let isDataChanged = false;
  const excludedFields = new Set(["createdBy", "createdAt", "updatedAt", "updatedBy"]);

  Object.keys(formData).forEach((key) => {
    if (!excludedFields.has(key) && shouldUpdateField(existingData[key], formData[key])) {
      fieldsToUpdate[key] = formData[key];
      isDataChanged = true;
    }
  });

  return isDataChanged ? fieldsToUpdate : null;
}

function shouldUpdateField(existingValue, newValue) {
  if (typeof newValue === "object" && !Array.isArray(newValue)) {
    return JSON.stringify(newValue) !== JSON.stringify(existingValue);
  } else if (Array.isArray(newValue)) {
    return JSON.stringify(newValue) !== JSON.stringify(existingValue);
  } else {
    return newValue !== existingValue;
  }
}

async function updateAppointmentsAndTimesheets(id, formData, currentUserId, batchUpdate, employeeData) {
  const relatedCollections = [COLLECTIONS.TIMESHEETS, COLLECTIONS.APPOINMENTS];
  const uniqueUserIds = new Set();

  for (const collectionName of relatedCollections) {
    const collectionRef = collection(firebase, collectionName);
    const q = query(collectionRef, where("clientId", "==", id));
    const querySnapshot = await getDocs(q);

    await Promise.all(
      querySnapshot.docs.map((docSnapshot) => {
        const userId = docSnapshot.data()?.userId;
        if (userId) {
          uniqueUserIds.add(userId);
        }
        return updateFormFields(docSnapshot, collectionName, formData, currentUserId, batchUpdate);
      })
    );
  }

  const uniqueUserIdArray = Array.from(uniqueUserIds);
  const relevantEmployees = employeeData.filter((emp) => uniqueUserIdArray.includes(emp.userId));

  return relevantEmployees;
}

// Update form fields in appointments or timesheets
async function updateFormFields(docSnapshot, collectionName, formData, currentUserId, batchUpdate) {
  const currentForms = collectionName === COLLECTIONS.APPOINMENTS
    ? docSnapshot.data().forms
    : docSnapshot.data().apoointmentDetails?.forms;

  const updatedForms = currentForms.map((form) => {
    if (form?.values) {
      const updatedValues = form.values.map((value) => updateFormValue(value, formData));
      const addedFields = addMissingAddressFields(form.values, formData);

      return {
        ...form,
        values: [...updatedValues, ...addedFields],
      };
    }
    return form;
  });

  const docRef = doc(firebase, collectionName, docSnapshot.id);
  batchUpdate.update(docRef, {
    [`${collectionName === COLLECTIONS.APPOINMENTS ? "forms" : "apoointmentDetails.forms"}`]: updatedForms,
    updatedAt: Timestamp.now(),
    updatedBy: currentUserId,
  });
}

// Update individual form value
function updateFormValue(value, formData) {
  switch (value.name) {
    case "Address":
      return { ...value, value: formData?.addressInfo?.address || value.value };
    case "City":
      return { ...value, value: formData?.addressInfo?.city || value.value };
    case "State":
      return { ...value, value: formData?.addressInfo?.state || value.value };
    case "ZipCode":
      return { ...value, value: formData?.addressInfo?.zip || value.value };
    default:
      return value;
  }
}

// Add missing address-related fields to the form
function addMissingAddressFields(values, formData) {
  const fieldNames = ["Address", "City", "State", "ZipCode"];
  const missingFields = [];

  fieldNames.forEach((fieldName) => {
    if (!values.some((value) => value.name === fieldName)) {
      missingFields.push({
        name: fieldName,
        value: formData?.addressInfo?.[fieldName.toLowerCase()] || "",
      });
    }
  });

  return missingFields;
}

const hasAddressChanged = (formAddress, existingAddress) => {
  return formAddress?.address !== existingAddress?.address ||
    formAddress?.city !== existingAddress?.city ||
    formAddress?.state !== existingAddress?.state ||
    formAddress?.zip !== existingAddress?.zip;
};

/**
 *  Update Data of perticular client
 * @param {*} id : id of client want to update
 * @param {*} formData  : only pass data should be updated
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function updateClient(id, formData, acuityClientData, onSuccess, onError) {
  const currentUser = auth.currentUser;
  const currentUserId = currentUser ? currentUser.uid : null;

  try {
    const clientDoc = doc(firebase, COLLECTIONS.CLIENT, id);
    const clientDocSnapshot = await getDoc(clientDoc);

    if (!clientDocSnapshot.exists()) {
      onError && onError({ status: false, message: "Client Not Found" });
      return;
    }

    const existingData = clientDocSnapshot.data();
    const fieldsToUpdate = getFieldsToUpdate(existingData, formData);

    if (!fieldsToUpdate || !Object.keys(fieldsToUpdate).length) {
      onSuccess && onSuccess({ status: true, message: "No changes detected", data: existingData });
      return { status: true, data: existingData };
    }

    // if (!existingData?.userCreation && fieldsToUpdate?.userCreation === true) {
    //   const password = generateP();
    //   let NewUserData = {
    //     firstname: existingData?.firstName,
    //     lastname: existingData?.lastName,
    //     email: fieldsToUpdate?.contact?.[0]?.email,
    //     phoneNumber: fieldsToUpdate?.contact?.[0]?.mobileNumber,
    //     password: password,
    //     companyId: existingData?.companyId,
    //   };

    //   const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };

    //     const res = await axiosInstance.post(
    //       `${BaseUrl}createUser`,
    //       { ...NewUserData },
    //       { headers: { ...defaultHeaders, ...authHeaders } }
    //     );

    //     if (res?.data?.status === true) {
    //       const newClientUser = {
    //         role: ROLES.CLIENT,
    //         userId: res?.data?.uid,
    //         firstName: existingData?.firstName || "",
    //         lastName: existingData?.lastName || "",
    //         email: fieldsToUpdate?.contact?.[0]?.email || "",
    //         userName: fieldsToUpdate?.contact?.[0]?.email || "",
    //         phoneNumber: fieldsToUpdate?.contact?.[0]?.mobileNumber || "",
    //         timeZone: existingData?.timeZone || "",
    //         companyId: existingData?.companyId || "",
    //         client: id,
    //         status: true,
    //       };

    //       const userDocRef = doc(firebase, COLLECTIONS.USERS, newClientUser?.userId);
    //       await setDoc(userDocRef, {
    //         ...newClientUser,
    //         createdBy: currentUserId,
    //         createdAt: new Date().getTime(),
    //         updatedAt: new Date().getTime(),
    //       });

    //     } else {
    //       let errorMessage = res?.data?.message || "An error occurred";
    //       if (res?.data?.error?.code === "auth/email-already-exists") {
    //         errorMessage = "This email is already registered. Please use another email.";
    //       }    
    //       onError && onError({ status: false, error: errorMessage });
    //       return { status: false, message: errorMessage };
    //     }
    // }

    const batchUpdate = writeBatch(firebase);
    const clientDocs = doc(firebase, COLLECTIONS.CLIENT, id);
    batchUpdate.update(clientDocs, {
      ...fieldsToUpdate,
      updatedAt: Timestamp.now(),
      updatedBy: currentUserId,
    });

    const isAddressChanged = hasAddressChanged(formData?.addressInfo, existingData?.addressInfo);

    if (isAddressChanged && formData?.addressInfo) {
      const employeeData = await getEmployeesData();
      const relevantEmployees = await updateAppointmentsAndTimesheets(id, formData, currentUserId, batchUpdate, employeeData);
      await sendNotifications(relevantEmployees, formData);
    }

    await batchUpdate.commit();

    // const clientUpdateResponse = await axiosInstance.post(`${BaseUrl}updateClientAcuity`, { acuityClientData });
    // console.log("Client Acuity updated:", clientUpdateResponse);

    onSuccess && onSuccess({ status: true, data: formData });
    return { status: true, data: formData };

  } catch (error) {
    console.error("Error updating client:", error);

    if (error.code === "not-found") {
      onError && onError({ status: false, message: "Client Not Found" });
    } else {
      onError && onError({ status: false, error: error.message || error });
    }
  }
}

async function getEmployeesData() {
  const empCollection = collection(firebase, COLLECTIONS.USERS);
  const empDocSnapshot = await getDocs(empCollection);

  if (empDocSnapshot.empty) throw new Error("No Employees Found");

  return empDocSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

async function sendNotifications(employees, formData) {
  const emailRequests = [];
  const pushRequests = [];

  employees?.forEach((emp) => {
    axiosInstance
      .post(`${BaseUrl}sendEmailData`, {
        email: emp.email,
        subject: "Your appointment with the ProTeams app has been updated to a new address.",
        contenttxt: `
        <div style="font-family: Arial, sans-serif; color: #333; max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 10px; overflow: hidden;">
            <div style="background-color:#f4f6f8; padding:20px; text-align:center;">
              <img
                src="https://${DOMAINURL}/csmGrayTextLogin.png"
                alt="ProTeams Logo"
                width="180"
                style="display:block; margin:0 auto; max-width:180px; width:100%; height:auto; border:0;"
              />
            </div>

            <div style="padding:25px 20px;">
        
        <p>Hello ${emp.firstName} ${emp.lastName} ,</p>
          <p></p>
          <p>Your scheduled appointments with <strong>${formData.clientName}</strong> has been updated to a new address. Please find the details below:</p> 
          <p></p>
          <p>
            Client Name : ${formData.clientName}<br />
            Address     : ${formData.addressInfo.address}, ${formData.addressInfo.state}, ${formData.addressInfo.city}, ${formData.addressInfo.zip}<br />
          </p>
          <p></p>
          <p>Please contact your support team if you have any questions.</p>
          <p></p>
          <p style="font-size: 14px; color: #666;">
          If you have any questions about using the Web Portal or need assistance getting started, please don't hesitate to reach out to our support team.
          </p>
          
          <hr style="border:none; border-top:1px solid #e5e7eb; margin:30px 0;">

              <p style="text-align:center; font-size:13px; color:#666; margin:0;">
                For assistance, feel free to <a href="https://${DOMAINURL}/help/support" style="color:#1a73e8; text-decoration:underline;">contact support</a>.<br>
                © ${new Date().getFullYear()} ProTeams. All rights reserved.
              </p>
          </div>
          </div>`,
        appointments: true,
      })
      .catch((err) => {
        console.error(`Failed to send email to ${emp.email}:`, err.message);
      });

    pushRequests.push(
      axiosInstance.post(`${BaseUrl}sendPushNotifications`, {
        userIds: emp.userId,
        title: "ProTeams - Alert",
        textMessage: "Your appointment with the ProTeams app has been updated to a new address.",
        extData: { type: "Appointments" },
      })
    );
  });

  await Promise.all(pushRequests);
}

export async function updateClientStatus(clientData, newStatus) {
  try {
    const clientRef = doc(firebase, COLLECTIONS.CLIENT, clientData.id);
    let updatedFields = { status: newStatus };

    if (newStatus === false) {
      const updatedContacts = (clientData?.contact || [])?.map(c => ({
        ...c,
        invite: false,
      }));
      updatedFields.contact = updatedContacts;
    }

    await updateDoc(clientRef, updatedFields, { merge: true });

    return {
      status: true,
      updatedData: updatedFields,
    };
  } catch (error) {
    console.error("Error updating client: ", error);
    return {
      status: false,
      error: error.message,
    };
  }
}

export async function deleteInvitedUsers(invitedEmails) {
  try {
    const userQuery = query(
      collection(firebase, "users"),
      where("email", "in", invitedEmails)
    );
    const snapshot = await getDocs(userQuery);

    for (const docSnap of snapshot.docs) {
      const userData = docSnap.data();
      const id = userData?.userId;
      const firstName = userData?.firstName;
      const email = userData?.email || "";

      await deleteDoc(docSnap.ref);

      const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
      const res = await axiosInstance.post(
        `${BaseUrl}removeUser`,
        { id, firstName, email },
        { headers: { ...defaultHeaders, ...authHeaders } }
      );
    }

    return { status: true };
  } catch (error) {
    console.error("Failed to delete invited users:", error);
    return { status: false, error: error.message };
  }
}

/**
 * Remove client data from document
 * @param {*} id : id of client
 * @param {*} onSuccess
 * @param {*} onError
 */

export async function deleteClient(id, acuityClientDatadele, onSuccess, onError) {
  const documentRef = doc(firebase, COLLECTIONS.CLIENT, id);

  try {
    await deleteDoc(documentRef);
    await axiosInstance.post(`${BaseUrl}deleteClientAcuity`, { acuityClientDatadele });

    onSuccess && onSuccess({ status: true, message: 'Delete successful' });
  } catch (error) {
    console.error('Error deleting client:', error);
    onError && onError({ status: false, error: error.message || 'An error occurred' });
  }
}

/**
 * Remove client data from document
 * @param {*} id : id of client
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function deleteClientTaskGroup(id, taskItem) {
  try {
    const updates = {};
    const shiftName = taskItem?.shift.shiftId;

    updates[`shiftData.${shiftName}`] = deleteField();

    // Optionally, update the timestamp
    // updates['updatedAt'] = serverTimestamp(); // Uncomment if you want to update the timestamp

    const docRef = doc(firebase, COLLECTIONS.CLIENT, id);
    await updateDoc(docRef, updates);

    return true;
  } catch (error) {
    console.error('Error deleting client task group:', error);
    return null;
  }
}

export async function deleteClientShift(id, taskItem) {
  try {
    const updates = {};
    const shiftName = taskItem?.shiftId;

    updates[`shiftData.${shiftName}`] = deleteField();

    // Optionally, update the timestamp
    // updates['updatedAt'] = serverTimestamp(); // Uncomment if you want to update the timestamp

    const docRef = doc(firebase, COLLECTIONS.CLIENT, id);
    await updateDoc(docRef, updates);
    return true;
  } catch (error) {
    console.error('Error deleting client shift:', error);
    return null;
  }
}
/**
 * Upload logo of client :: not used for now need to change logo upload
 * @param {*} file
 * @param {*} onSuccess
 */
export async function uploadLogo(file, companyId) {
  return new Promise((resolve, reject) => {
    try {
      const storage = getStorage();
      const storageRef = ref(storage, `${companyId}/clients/${file?.name}`);

      const metadata = {
        contentType: 'image/jpeg',
      };

      const uploadTask = uploadBytesResumable(storageRef, file, metadata);

      uploadTask.on(
        'state_changed',
        null,
        (error) => {
          console.error('Upload failed:', error);
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          } catch (urlError) {
            console.error('Failed to get download URL:', urlError);
            reject(urlError);
          }
        }
      );
    } catch (error) {
      console.error('Failed to upload logo:', error);
      reject(error);
    }
  });
}

/**
 * Get Document list from client collection for summary
 * @param {*} formData : can be use for filter in future
 * @param {*} onSuccess
 * @param {*} onError
 */
// export const getClientsSummary = (formData, onSuccess, onError) => {
//   let colRef = firebase.firestore().collection(collection);
//   colRef
//     .limit(5)
//     .get()
//     .then((querySnapshot) => {
//       let results = [];
//       querySnapshot.forEach((doc) => {
//         let data = doc.data();
//         results.push({ id: doc.id, ...data });
//       });
//       onSuccess && onSuccess(results);
//     })
//     .catch((error) => {
//       console.log('Error getting documents: ', error);
//       onError && onError(error);
//     });
// };

// export const updateClientNumbers = async () => {
//   const colRef = collection(firebase, COLLECTIONS.CLIENT);

//   try {
//     const querySnapshot = await getDocs(colRef);
//     let clientNumber = 101;

//     for (const docSnapshot of querySnapshot.docs) {
//       const docRef = doc(firebase, COLLECTIONS.CLIENT, docSnapshot.id);
//       await updateDoc(docRef, {
//         clientNumber: clientNumber,
//       });
//       clientNumber++;
//     }

//     console.log("Client numbers have been successfully updated!");
//   } catch (error) {
//     console.error("Error updating client numbers:", error);
//   }
// };

// export const updateTimesheetRecords = async () => {
//   try {
//     const snapshot = await getDocs(collection(firebase, COLLECTIONS.TIMESHEETS));

//     for (let i = 0; i < snapshot.docs.length; i++) {
//       const doc = snapshot.docs[i];
//       const record = doc.data();
//       const appointmentDateTime = record.appointmentDateTime;
//       const timeZone = record.apoointmentDetails.calendarTimezone || "America/New_York";

//       const date = moment(appointmentDateTime).tz(timeZone);
//       const formattedDate = date.format("MMMM D, YYYY");

//       await updateDoc(doc.ref, { appointmentDate: formattedDate });

//       console.log(`Updated record ID: ${doc.id} with appointmentDate: ${formattedDate}`);
//     }

//     console.log('All records updated successfully.');
//   } catch (error) {
//     console.error('Error updating records:', error);
//   }
// };


// export async function addCompanyIdToAllDocuments() {
//   const collectionRef = collection(firebase, COLLECTIONS.TIMESHEETS);
//   const querySnapshot = await getDocs(collectionRef);

//   querySnapshot.forEach(async (docSnap) => {
//     const docRef = doc(firebase, COLLECTIONS.TIMESHEETS, docSnap.id);
//     try {
//       await updateDoc(docRef, {
//         companyId: "ckMwUiLZrTiVpJ6RIELG",
//       });
//       console.log(`Updated document with ID: ${docSnap.id}`);
//     } catch (error) {
//       console.error("Error updating document: ", error);
//     }
//   });
// }

// const docIds = [
//   "4q9SywMIPLmmLiEkcR5t",
//   "6fjVUXYObyYGC3DpF0sh",
//   "CZ6m99CKCOMnNo8FpHy8",
//   "E4v4dAXbuGVxYdvZPosF",
//   "LQUQT9EFdlLoUYSQGt2J",
//   "ch79WTmTAP1nid3Lz0xD",
//   "g9RQZEtOgGtFk3HNsGY8",
//   "nLLPbofSLbxXRnNaMzGo",
//   "pY12be1lD1KhnVJ3M430",
//   "Ilmb4CeITi5nKr8TUUsJ",
//   "fFE9O1bQ4WPs9MjP3WtL",
//   "pqp7HHA7u9hCUrZvF8wC",
//   "t8D5D1xvwV8NuuSpsZbV",
//   "ZreImUsPP9q3sUCllYRG",
//   "fAsi7e93bdPSmdsPJ76j",
//   "hVUwuVPybP1LAeUEdR1p",
//   "7yUXujTFX73K4kmjxLHL",
//   "frVXJkm5IXOdw0fgYEWM",
//   "dmoaCx65tgGo3HhoJQx1"
// ];
// // const docIds = [
// //   "8U0ccpy3zjPs3tXVH7YTmjcOcqG3",
// //   "8wRXvx8KmyOmEhyEbkwXEqN3ls23",
// //   "9ERAEb45wce83MhBzi1JnZSh0vJ2",
// //   "FEjRQsMWbqWx0jNmDjZt4wuOt5v1",
// //   "MsdSf35GDUb7sC0rwtbxszrNIAW2",
// //   "Y3KhlavnSPUxU7VAsY7jo1mSjXz1",
// //   "ZQBjICu1BHRJYPRes7a5tO3DJUx1",
// //   "fvOAJAN6rUXtIp8ExzIKjW57FHE2",
// //   "jCjOEbIhJthvQRCOdurV1vMx0re2",
// //   "kn2gdoIBtlefHaqGTjGbNpdTBYg1",
// //   "lSdMWvPh7UdnRvHcB5FMwk48eiv2",
// //   "ps7eTBY4rwWRIXAjA2XRIQqP7Qt1",
// //   "rbDBd5jUOiguy3SAjWcMzVN9GAg1"
// // ];

// // const docIds = [
// //   "7KUVijMdnYS89XITLWJQkuGfKKn2",
// //   "Cv50jes1SjTE24QLqanwQr4qA972",
// //   "K4esopz4HWYTVp6jTPO2qB2AAn22",
// //   "K5zq3yhFL2UW0wVLQwE7btGsVVW2",
// //   "NkA855N17LWqzhVedfhIwGCGNvR2",
// //   "RgOPo4Z5aGND4ByyJV1Xi34kdkt1",
// //   "cZYArGN5wUgzzRDXlEBbl5g6sR33",
// //   "f8BNZSxpmEaO1XXGmscNvUbFRF92",
// //   "rcIfNQHwgJSX9qhba3y2teHDulg1"
// // ];


// const companyId = "3orU27bKPZUGb7a8UfHPaErtLvB3"
// // const companyId = "GwjuN0VkS8ff88AKepYaQTdLG5t1"

// const BATCH_LIMIT = 480;
// const PAGE_SIZE = 200;

// export async function bulkApplyCompanyIdToGroups(
// ) {
//   const channelsRef = collection(firebase, COLLECTIONS.GROUPS);
//   let updated = 0;
//   let skipped = 0;

//   const chunk = (arr, size) => {
//     const out = [];
//     for (let i = 0; i < arr.length; i += size) {
//       out.push(arr.slice(i, i + size));
//     }
//     return out;
//   };
//   const idChunks = chunk(docIds, 10);

//   let batch = writeBatch(firebase);
//   let opsInBatch = 0;

//   for (const chunkIds of idChunks) {
//     const q = query(
//       channelsRef,
//       where("employeeId", "array-contains-any", chunkIds)
//     );
//     const snap = await getDocs(q);
//     for (const channelDoc of snap.docs) {
//       const data = channelDoc.data();
//       const currentCompanyId = data?.companyId;
//       if (currentCompanyId === companyId) {
//         skipped += 1;
//         continue;
//       }
//       const channelRef = doc(firebase, COLLECTIONS.GROUPS, channelDoc.id);
//       batch.update(channelRef, { companyId: companyId });
//       updated += 1;
//       opsInBatch += 1;

//       if (opsInBatch >= BATCH_LIMIT) {
//         await batch.commit();
//         batch = writeBatch(firebase);
//         opsInBatch = 0;
//       }
//     }
//   }

//   if (opsInBatch > 0) {
//     await batch.commit();
//   }

//   return { updated, skipped };
// }

// export async function bulkApplyCompanyIdToChannels() {
//   const channelsRef = collection(firebase, COLLECTIONS.CHAT_CHANNELS);
//   const idSet = new Set(docIds);
//   let updated = 0;
//   let skipped = 0;

//   let batch = writeBatch(firebase);
//   let opsInBatch = 0;

//   let lastDoc = null;

//   while (true) {
//     let q;
//     if (lastDoc) {
//       q = query(
//         channelsRef,
//         orderBy("__name__"),
//         startAfter(lastDoc),
//         limit(PAGE_SIZE)
//       );
//     } else {
//       q = query(channelsRef, orderBy("__name__"), limit(PAGE_SIZE));
//     }

//     const snap = await getDocs(q);
//     if (snap.empty) break;

//     for (const channelDoc of snap.docs) {
//       const data = channelDoc.data();
//       const currentCompanyId = data?.companyId;
//       const employees = Array.isArray(data?.employee) ? data.employee : [];

//       const hasMatch = employees.some((emp) => emp?.id && idSet.has(emp.id));
//       if (!hasMatch) continue;

//       if (currentCompanyId === companyId) {
//         skipped += 1;
//         continue;
//       }

//       const channelRef = doc(firebase, COLLECTIONS.CHAT_CHANNELS, channelDoc.id);
//       batch.update(channelRef, { companyId: companyId });
//       updated += 1;
//       opsInBatch += 1;

//       if (opsInBatch >= BATCH_LIMIT) {
//         await batch.commit();
//         batch = writeBatch(firebase);
//         opsInBatch = 0;
//       }
//     }

//     lastDoc = snap.docs[snap.docs.length - 1];
//     if (snap.docs.length < PAGE_SIZE) break;
//   }

//   if (opsInBatch > 0) {
//     await batch.commit();
//   }

//   return { updated, skipped };
// }


// export async function bulkApplyCompanyId() {

//   const appointmentsRef = collection(firebase, COLLECTIONS.SUPPLIES_ORDER_NOTES);
//   let updated = 0;
//   let skipped = 0;

//   const chunk = (arr, size) => {
//     const out = [];
//     for (let i = 0; i < arr.length; i += size) {
//       out.push(arr.slice(i, i + size));
//     }
//     return out;
//   };

//   const idChunks = chunk(docIds, 10);

//   let batch = writeBatch(firebase);
//   let opsInBatch = 0;

//   const flushBatch = async () => {
//     if (opsInBatch > 0) {
//       try {
//         await batch.commit();
//       } catch (err) {
//         console.error("Batch commit failed:", err);
//         throw err;
//       }
//       batch = writeBatch(firebase);
//       opsInBatch = 0;
//     }
//   };

//   for (const idChunk of idChunks) {

//     const q = query(appointmentsRef, where("createdBy", "in", idChunk));
//     const snap = await getDocs(q);

//     for (const apptDoc of snap.docs) {
//       const data = apptDoc.data();
//       const currentCompanyId = data?.companyId;
//       if (currentCompanyId !== companyId) {
//         const apptRef = doc(firebase, COLLECTIONS.SUPPLIES_ORDER_NOTES, apptDoc.id);
//         batch.update(apptRef, { companyId });
//         updated += 1;
//         opsInBatch += 1;
//       } else {
//         skipped += 1;
//       }

//       if (opsInBatch >= 480) {
//         await flushBatch();
//       }
//     }
//   }

//   await flushBatch();

//   return { updated, skipped };
// }

// export async function syncShiftInfoIntoCustomTasks(clientId) {

//   const clientRef = doc(firebase, COLLECTIONS.CLIENT, clientId);
//   const snap = await getDoc(clientRef);
//   if (!snap.exists()) {
//     throw new Error(`Client document with ID "${clientId}" does not exist`);
//   }

//   const data = snap.data();
//   if (!data.shiftData || !data.customTaskList) {
//     console.warn("Document missing shiftData or customTaskList; nothing to sync.");
//     return { success: false, reason: "missing_fields" };
//   }

//   const updatedCustom = { ...data.customTaskList };

//   Object.entries(data.shiftData).forEach(([shiftId, shiftTasksObj]) => {
//     Object.values(shiftTasksObj || {}).forEach((shifttask) => {
//       const frequency = shifttask?.frequency;
//       if (!shifttask?.tasksList || !Array.isArray(shifttask.tasksList)) return;

//       shifttask.tasksList.forEach((task) => {
//         const taskId = task?.taskId;
//         if (!taskId) return;
//         if (!updatedCustom[taskId]) {
//           console.warn(`customTaskList missing entry for taskId "${taskId}", skipping`); 
//           return;
//         }
//         updatedCustom[taskId] = {
//           ...updatedCustom[taskId],
//           frequency: frequency ?? updatedCustom[taskId]?.frequency,
//           shift: shiftId,
//         };
//       });
//     });
//   });

//   await updateDoc(clientRef, {
//     customTaskList: updatedCustom,
//   });

//   return { success: true };
// }

// export async function backfillCustomTasksIntoShift(clientId, shiftId, shifttaskKey) {
//   if (!clientId || !shiftId || !shifttaskKey) {
//     throw new Error("clientId, shiftId and shifttaskKey are required");
//   }

//   const clientRef = doc(firebase, COLLECTIONS.CLIENT, clientId);
//   const snap = await getDoc(clientRef);
//   if (!snap.exists()) {
//     throw new Error(`Client document with ID "${clientId}" does not exist`);
//   }

//   const data = snap.data();
//   if (!data.shiftData || !data.customTaskList) {
//     console.warn("Missing shiftData or customTaskList; nothing to do.");
//     return { success: false, reason: "missing_fields" };
//   }

//   const updatedCustom = { ...data.customTaskList };
//   const updatedShiftData = { ...data.shiftData };

//   const shiftObj = updatedShiftData[shiftId];
//   if (!shiftObj) {
//     throw new Error(`Shift ID "${shiftId}" not found in shiftData`);
//   }

//   const shifttask = shiftObj[shifttaskKey];
//   if (!shifttask) {
//     throw new Error(`Shifttask key "${shifttaskKey}" not found under shift "${shiftId}"`);
//   }

//   if (!Array.isArray(shifttask.tasksList)) {
//     shifttask.tasksList = [];
//   }

//   const existingTaskIds = new Set(shifttask.tasksList.map((t) => t.taskId).filter(Boolean));
//   const frequency = shifttask.frequency || "";

//   let addedToShift = 0;
//   let updatedCustomCount = 0;

//   Object.entries(updatedCustom).forEach(([taskId, customTask]) => {
//     if (!taskId) return;
//     if (existingTaskIds.has(taskId)) return;

//     shifttask.tasksList.push({
//       taskId,
//       title: customTask.title || "",
//     });
//     addedToShift += 1;

//     const prevFreq = customTask.frequency;
//     const prevShift = customTask.shift;
//     updatedCustom[taskId] = {
//       ...customTask,
//       frequency: frequency || prevFreq || "",
//       shift: shiftId,
//     };
//     updatedCustomCount += 1;
//   });

//   updatedShiftData[shiftId] = {
//     ...shiftObj,
//     [shifttaskKey]: shifttask,
//   };

//   await updateDoc(clientRef, {
//     shiftData: updatedShiftData,
//     customTaskList: updatedCustom,
//   });

//   return {
//     success: true,
//     addedToShift,
//     updatedCustom: updatedCustomCount,
//   };
// }

// export async function importCollection() {
//   try {
//     const collectionName = COLLECTIONS.SUPPLIES_INDUSTRY;
//     const collectionRef = collection(firebase, collectionName);

//     const addPromises = [];

//     for (const item of serviceJson) {
//      const docRef = doc(collectionRef, item.id);
//      const addPromise = setDoc(docRef, item);
//       addPromises.push(addPromise);
//     }

//     await Promise.all(addPromises);

//     console.log(`${addPromises.length} documents imported to Firestore using existing IDs!`);
//   } catch (error) {
//     console.error("Error importing data to Firestore:", error);
//   }
// }

// export async function ensureClientTaskIntervals() {
//   const colRef = collection(firebase, COLLECTIONS.CLIENT);
//   const querySnapshot = await getDocs(colRef);

//   for (const clientDoc of querySnapshot.docs) {
//     const clientId = clientDoc.id;
//     const clientData = clientDoc.data();

//     if (!clientData.customTaskList) {
//       console.log(`Client ${clientId} has no customTaskList, skipping.`);
//       continue;
//     }

//     let updated = false;
//     const customTaskList = { ...clientData.customTaskList };

//     for (const [taskId, task] of Object.entries(customTaskList)) {
//       if (task && task.interval == null) {
//         customTaskList[taskId] = { ...task, interval: 30 };
//         updated = true;
//         console.log(`Client ${clientId} → Added interval 30 to task ${taskId}`);
//       }
//     }

//     if (updated) {
//       const clientRef = doc(firebase, COLLECTIONS.CLIENT, clientId); // ✅ correct reference
//       await updateDoc(clientRef, { customTaskList }); // ✅ correct update
//       console.log(`Client ${clientId} updated.`);
//     } else {
//       console.log(`Client ${clientId} already has intervals, no update needed.`);
//     }
//   }

//   console.log("Finished processing all clients.");
// }


// export async function updateAllCompanyUsersVerified() {
//   try {
//      const userRef = collection(firebase, COLLECTIONS.USERS);
//       const q = query(userRef, where("role", "==", "companyUser"));
//      const snap = await getDocs(q);

//     if (snap.empty) {
//       console.log("No company users found.");
//       return;
//     }

//     let updatedCount = 0;
//     for (const doc of snap.docs) {
//       await updateDoc(doc.ref, { verified: true });
//       updatedCount++;
//       console.log(`Updated: ${doc.id}`);
//     }

//     console.log(` Successfully updated ${updatedCount} company user(s).`);
//   } catch (err) {
//     console.error(" Error updating users:", err);
//   }
// }

/**
 * Clear tasksList for all clients of a company when industry changes.
 * Excludes shifts that are "opentasks" variants.
 * @param {string} companyId 
 */
export async function clearClientTasksOnIndustryChange(companyId) {
  try {
    const clientsQuery = query(
      collection(firebase, COLLECTIONS.CLIENT),
      where("companyId", "==", companyId)
    );

    const querySnapshot = await getDocs(clientsQuery);

    if (querySnapshot.empty) {
      return { status: true, message: "No clients found for this company" };
    }

    const batch = writeBatch(firebase);
    let hasChanges = false;

    querySnapshot.docs.forEach((clientDoc) => {
      const clientData = clientDoc.data();
      const shiftData = clientData.shiftData;

      if (shiftData) {
        let clientUpdated = false;
        const updatedShiftData = JSON.parse(JSON.stringify(shiftData));

        Object.keys(updatedShiftData).forEach((shiftId) => {
          const shift = updatedShiftData[shiftId];
          Object.keys(shift).forEach((taskKey) => {
            if (taskKey.startsWith("shifttask")) {
              const shiftTask = shift[taskKey];
              const title = shiftTask.title || "";

              if (!title.toLowerCase().includes("opentasks")) {
                if (shiftTask.tasksList && shiftTask.tasksList.length > 0) {
                  shiftTask.tasksList = [];
                  clientUpdated = true;
                }
              }
            }
          });
        });

        if (clientUpdated) {
          const docRef = doc(firebase, COLLECTIONS.CLIENT, clientDoc.id);
          batch.update(docRef, {
            shiftData: updatedShiftData,
            updatedAt: serverTimestamp()
          });
          hasChanges = true;
        }
      }
    });

    if (hasChanges) {
      await batch.commit();
    }

    return { status: true, message: "Client tasks cleared successfully" };
  } catch (error) {
    console.error("Error clearing client tasks:", error);
    return { status: false, error: error.message };
  }
}
